import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { LogOut, Clock, Shield, Box, QrCode, Wallet, MessageSquare, Briefcase, Target, LayoutTemplate, Calculator, Package } from 'lucide-react';
import useWindowWidth from '../hooks/useWindowWidth';

const API = process.env.REACT_APP_API_URL;

const ICONS = { arbeitszeit: Clock, admin: Shield, qrcode: QrCode, ausgaben: Wallet, kontakt: MessageSquare, essentials: Briefcase, akquise: Target, templates: LayoutTemplate, rechner: Calculator, installer: Package };
const LINKS = { arbeitszeit: '/privat/arbeitszeit', admin: '/privat/admin', qrcode: '/privat/qrcode', ausgaben: '/privat/ausgaben', kontakt: '/privat/kontakt', essentials: '/privat/essentials', akquise: '/privat/akquise', templates: '/privat/templates', rechner: '/privat/rechner', installer: '/privat/exe' };
const DESCRIPTIONS = {
    arbeitszeit: 'Arbeitszeiten für Tobi und Lino eintragen und auswerten.',
    admin: 'User verwalten und Seitenbesucher einsehen.',
    qrcode: 'QR-Codes für URLs, Text oder beliebige Inhalte generieren.',
    ausgaben: 'Ausgaben pro Monat nach Kategorie und Person erfassen.',
    kontakt: 'Eingehende Kontaktanfragen vom Portfolio ansehen und verwalten.',
    essentials: 'Kunden, Projekte, Rechnungen und Leads verwalten.',
    akquise: 'Cold-E-Mail CRM für Leads und Outreach verwalten.',
    templates: 'Vorlagen für Kundenprojekte ansehen, hochladen und herunterladen.',
    rechner: 'Aufwand erfassen und automatisch einen Angebotspreis berechnen.',
    installer: 'Programme für Windows auswählen und Installer erstellen.',
};

const flipCSS = `
@keyframes flipDown {
    0%   { transform: rotateX(0deg); }
    100% { transform: rotateX(-90deg); }
}
@keyframes flipUp {
    0%   { transform: rotateX(90deg); }
    100% { transform: rotateX(0deg); }
}
@keyframes shadowIn {
    0%   { opacity: 0; }
    100% { opacity: 0.55; }
}
@keyframes shadowOut {
    0%   { opacity: 0.55; }
    100% { opacity: 0; }
}
@keyframes overlayIn {
    0%   { opacity: 0; }
    100% { opacity: 1; }
}
@keyframes overlayOut {
    0%   { opacity: 1; }
    100% { opacity: 0; }
}
`;

function FlipCard({ value, label, small }) {
    const [curr, setCurr] = useState(value);
    const [nxt, setNxt]   = useState(value);
    const [phase, setPhase] = useState('idle'); // 'idle' | 'flipping'
    const fmt = v => String(v).padStart(2, '0');

    useEffect(() => {
        if (value === curr) return;
        setNxt(value);
        setPhase('flipping');
    }, [value]);

    const W = small ? 52 : 80;
    const H = small ? 66 : 100;
    const HALF = H / 2;
    const fontSize = small ? 32 : 54;

    const topStyle = {
        position: 'absolute', top: 0, left: 0, right: 0, height: HALF,
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #2a3352 0%, #1e2844 100%)',
        borderRadius: '8px 8px 0 0',
    };
    const botStyle = {
        position: 'absolute', bottom: 0, left: 0, right: 0, height: HALF,
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #151d36 0%, #1b2342 100%)',
        borderRadius: '0 0 8px 8px',
    };

    const num = val => (
        <div style={{ height: H, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{
                fontSize, fontWeight: 800,
                color: 'var(--accent)',
                fontFamily: '"Courier New", Courier, monospace',
                letterSpacing: '-2px',
                textShadow: '0 0 24px rgba(251,146,60,0.25)',
            }}>
                {fmt(val)}
            </span>
        </div>
    );

    const flipping = phase === 'flipping';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: small ? 8 : 12 }}>
            <div style={{
                position: 'relative', width: W, height: H,
                perspective: '300px',
                borderRadius: '8px',
                boxShadow: '0 10px 40px rgba(0,0,0,0.6), 0 2px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}>
                {/* Statische obere Hälfte — zeigt curr (wird durch animierte Klappe verdeckt) */}
                <div style={topStyle}>{num(curr)}</div>

                {/* Statische untere Hälfte — zeigt nxt während flip, danach curr */}
                <div style={botStyle}>
                    <div style={{ marginTop: -HALF }}>{num(flipping ? nxt : curr)}</div>
                </div>

                {/* Schatten auf unterer Hälfte während Flip */}
                {flipping && (
                    <div style={{
                        ...botStyle, zIndex: 3,
                        background: 'rgba(0,0,0,0.55)',
                        animation: 'shadowIn 0.20s ease-in forwards, shadowOut 0.20s ease-out 0.20s forwards',
                    }} />
                )}

                {/* Alte Klappe klappt runter (curr) */}
                {flipping && (
                    <div style={{
                        ...topStyle,
                        transformOrigin: 'bottom center',
                        animation: 'flipDown 0.22s cubic-bezier(0.4,0,1,1) forwards',
                        zIndex: 4,
                    }}>
                        {num(curr)}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.5) 100%)', animation: 'overlayIn 0.22s ease-in forwards' }} />
                    </div>
                )}

                {/* Neue Klappe klappt hoch (nxt) — startet gleichzeitig mit Delay */}
                {flipping && (
                    <div
                        onAnimationEnd={() => { setCurr(nxt); setPhase('idle'); }}
                        style={{
                            ...topStyle,
                            transformOrigin: 'bottom center',
                            animation: 'flipUp 0.22s cubic-bezier(0,0,0.4,1) 0.18s both',
                            zIndex: 5,
                        }}
                    >
                        {num(nxt)}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.35) 100%)', animation: 'overlayOut 0.22s ease-out 0.18s both' }} />
                    </div>
                )}

                {/* Mittelstrich */}
                <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: '2px', background: 'rgba(0,0,0,0.9)', transform: 'translateY(-50%)', zIndex: 6, boxShadow: '0 1px 4px rgba(0,0,0,0.8)' }} />
                {/* Highlight oben */}
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px 8px 0 0', zIndex: 7 }} />
            </div>
            <span style={{ fontSize: small ? '8px' : '10px', letterSpacing: '3px', color: 'var(--text-secondary)', fontWeight: 700, textTransform: 'uppercase' }}>
                {label}
            </span>
        </div>
    );
}

export default function PrivatePage() {
    const { user, logout, hasPermission } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === 'admin';
    const token = user?.token;
    const windowWidth = useWindowWidth();
    const small = windowWidth < 480;

    const [bereiche, setBereiche] = useState([]);
    const [countdown, setCountdown] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        fetch(`${API}/api/countdown`)
            .then(r => r.json())
            .then(cd => { if (cd.target) setCountdown(cd); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!countdown?.target) return;
        const calc = () => {
            const diff = new Date(countdown.target) - new Date();
            if (diff <= 0) { setTimeLeft(null); return; }
            setTimeLeft({
                d: Math.floor(diff / 86400000),
                h: Math.floor((diff % 86400000) / 3600000),
                m: Math.floor((diff % 3600000) / 60000),
                s: Math.floor((diff % 60000) / 1000),
            });
        };
        calc();
        const id = setInterval(calc, 1000);
        return () => clearInterval(id);
    }, [countdown]);

    useEffect(() => {
        fetch(`${API}/api/bereiche`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json())
            .then(setBereiche)
            .catch(() => { });
    }, [token]);

    const handleLogout = () => { logout(); navigate('/'); };

    const toggle = async (key, current) => {
        await fetch(`${API}/api/admin/bereiche/${key}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ enabled: !current })
        });
        fetch(`${API}/api/bereiche`, { headers: { Authorization: `Bearer ${token}` } })
            .then(r => r.json()).then(setBereiche);
    };

    const visible = isAdmin
        ? bereiche
        : bereiche.filter(b => b.enabled && hasPermission(b.key));

    return (
        <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
            <style>{flipCSS}</style>
            <div className="container">
                {countdown && timeLeft && (
                    <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
                        style={{ textAlign: 'center', marginBottom: '48px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '3px', color: 'var(--accent)', textTransform: 'uppercase', marginBottom: '24px', opacity: 0.8 }}>
                            {countdown.title}
                        </div>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center',
                            gap: small ? '5px' : '10px',
                            background: 'rgba(255,255,255,0.02)',
                            border: '1px solid rgba(255,255,255,0.05)',
                            borderRadius: small ? '12px' : '16px',
                            padding: small ? '16px 14px' : '28px 36px',
                            boxShadow: '0 4px 32px rgba(0,0,0,0.3)',
                        }}>
                            <FlipCard value={timeLeft.d} label="TAGE" small={small} />
                            <span style={{ fontSize: small ? '20px' : '36px', fontWeight: 800, color: 'var(--text-secondary)', opacity: 0.25, marginBottom: small ? '14px' : '22px', lineHeight: 1, flexShrink: 0 }}>:</span>
                            <FlipCard value={timeLeft.h} label="STD" small={small} />
                            <span style={{ fontSize: small ? '20px' : '36px', fontWeight: 800, color: 'var(--text-secondary)', opacity: 0.25, marginBottom: small ? '14px' : '22px', lineHeight: 1, flexShrink: 0 }}>:</span>
                            <FlipCard value={timeLeft.m} label="MIN" small={small} />
                            <span style={{ fontSize: small ? '20px' : '36px', fontWeight: 800, color: 'var(--text-secondary)', opacity: 0.25, marginBottom: small ? '14px' : '22px', lineHeight: 1, flexShrink: 0 }}>:</span>
                            <FlipCard value={timeLeft.s} label="SEK" small={small} />
                        </div>
                    </motion.div>
                )}

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
                        <h1 className="section-title" style={{ marginBottom: 0 }}>Privater Bereich</h1>
                        <button onClick={handleLogout} className="icon-button" style={{ display: 'flex', gap: '8px', alignItems: 'center', color: 'var(--text-secondary)' }}>
                            <LogOut size={18} />
                            <span style={{ fontSize: '14px', fontWeight: 500 }}>Abmelden</span>
                        </button>
                    </div>
                    <p className="section-subtitle">Willkommen, {user?.username}</p>
                </motion.div>

                <div className="projects-grid">
                    {visible.map((item, index) => {
                        const Icon = ICONS[item.key] || Box;
                        const isDisabled = !item.enabled;
                        return (
                            <motion.div
                                key={item.key}
                                className="project-card"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.6, delay: index * 0.1 }}
                                style={{ opacity: isDisabled ? 0.5 : 1 }}
                            >
                                <div className="project-image" style={{ position: 'relative' }}>
                                    <Icon size={56} strokeWidth={1.5} color="var(--text-secondary)" />
                                    {isAdmin && (
                                        <button
                                            onClick={() => toggle(item.key, item.enabled)}
                                            style={{
                                                position: 'absolute', top: '12px', right: '12px',
                                                fontSize: '11px', fontWeight: 600,
                                                color: item.enabled ? '#00ff94' : 'var(--text-secondary)',
                                                background: item.enabled ? 'rgba(0,255,148,0.1)' : 'var(--bg-secondary)',
                                                border: `1px solid ${item.enabled ? '#00ff94' : 'var(--border)'}`,
                                                padding: '3px 10px', borderRadius: '10px', cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {item.enabled ? 'Aktiv' : 'Inaktiv'}
                                        </button>
                                    )}
                                    {!isAdmin && isDisabled && (
                                        <div style={{ position: 'absolute', top: '12px', right: '12px', fontSize: '11px', fontWeight: 600, color: 'var(--text-secondary)', background: 'var(--bg-secondary)', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: '10px' }}>
                                            Inaktiv
                                        </div>
                                    )}
                                </div>
                                <div className="project-content">
                                    <h3 className="project-title">{item.title}</h3>
                                    <p className="project-description">{DESCRIPTIONS[item.key] || ''}</p>
                                    <div className="project-links">
                                        {item.enabled ? (
                                            <Link to={LINKS[item.key] || '#'} className="project-link" style={{ flex: 1, justifyContent: 'center' }}>
                                                Öffnen
                                            </Link>
                                        ) : (
                                            <span className="project-link" style={{ flex: 1, justifyContent: 'center', opacity: 0.4, cursor: isAdmin ? 'pointer' : 'not-allowed' }}
                                                onClick={isAdmin ? () => toggle(item.key, false) : undefined}>
                                                {isAdmin ? 'Aktivieren' : 'Inaktiv'}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>

                {visible.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '80px 0', color: 'var(--text-secondary)' }}>
                        Kein Zugriff auf Bereiche.
                    </div>
                )}
            </div>
        </div>
    );
}